/**
 * Informational Data Service
 * Provides realistic demo data for popular destinations, weather, travel tips, and enhanced offers
 */

import { 
  PopularDestination, 
  WeatherInfo, 
  DestinationWeather, 
  TravelTip, 
  EnhancedOffer 
} from '../types/informational';

export class InformationalDataService {
  private static instance: InformationalDataService;

  public static getInstance(): InformationalDataService {
    if (!InformationalDataService.instance) {
      InformationalDataService.instance = new InformationalDataService();
    }
    return InformationalDataService.instance;
  }

  /**
   * Get popular European destinations with realistic data
   */
  public getPopularDestinations(): PopularDestination[] {
    return [
      {
        id: 'paris-fr',
        name: 'Paris',
        country: 'France',
        code: 'CDG',
        coordinates: { lat: 48.8566, lng: 2.3522 },
        popularity: 95,
        averagePrice: 299,
        currency: 'EUR',
        image: '/assets/images/destinations/paris.jpg',
        description: 'The City of Light awaits you with its iconic monuments and refined gastronomy.',
        highlights: ['Eiffel Tower', 'Louvre', 'Champs-Élysées', 'Montmartre'],
        bestTimeToVisit: 'April - June, September - October'
      },
      {
        id: 'london-uk',
        name: 'London',
        country: 'United Kingdom',
        code: 'LHR',
        coordinates: { lat: 51.5074, lng: -0.1278 },
        popularity: 92,
        averagePrice: 249,
        currency: 'EUR',
        image: '/assets/images/destinations/london.jpg',
        description: 'Discover British history and modern culture in this dynamic metropolis.',
        highlights: ['Big Ben', 'British Museum', 'Tower Bridge', 'Camden Market'],
        bestTimeToVisit: 'May - September'
      },
      {
        id: 'rome-it',
        name: 'Rome',
        country: 'Italy',
        code: 'FCO',
        coordinates: { lat: 41.9028, lng: 12.4964 },
        popularity: 89,
        averagePrice: 199,
        currency: 'EUR',
        image: '/assets/images/destinations/rome.jpg',
        description: 'Dive into the ancient history of the Eternal City and savor the dolce vita.',
        highlights: ['Colosseum', 'Vatican', 'Trevi Fountain', 'Roman Forum'],
        bestTimeToVisit: 'April - June, September - October'
      },
      {
        id: 'barcelona-es',
        name: 'Barcelona',
        country: 'Spain',
        code: 'BCN',
        coordinates: { lat: 41.3851, lng: 2.1734 },
        popularity: 87,
        averagePrice: 179,
        currency: 'EUR',
        image: '/assets/images/destinations/barcelona.jpg',
        description: 'Unique Gaudí architecture, Mediterranean beaches and vibrant nightlife.',
        highlights: ['Sagrada Familia', 'Park Güell', 'Las Ramblas', 'Barceloneta Beach'],
        bestTimeToVisit: 'May - June, September - October'
      },
      {
        id: 'amsterdam-nl',
        name: 'Amsterdam',
        country: 'Netherlands',
        code: 'AMS',
        coordinates: { lat: 52.3676, lng: 4.9041 },
        popularity: 84,
        averagePrice: 219,
        currency: 'EUR',
        image: '/assets/images/destinations/amsterdam.jpg',
        description: 'Picturesque canals, world-renowned museums and relaxed atmosphere.',
        highlights: ['Van Gogh Museum', 'Rijksmuseum', 'Jordaan', 'Vondelpark'],
        bestTimeToVisit: 'April - October'
      },
      {
        id: 'prague-cz',
        name: 'Prague',
        country: 'Czech Republic',
        code: 'PRG',
        coordinates: { lat: 50.0755, lng: 14.4378 },
        popularity: 81,
        averagePrice: 149,
        currency: 'EUR',
        image: '/assets/images/destinations/prague.jpg',
        description: 'Gothic and baroque architecture in a fairy-tale city.',
        highlights: ['Prague Castle', 'Charles Bridge', 'Old Town', 'Astronomical Clock'],
        bestTimeToVisit: 'May - September'
      }
    ];
  }

  /**
   * Generate realistic weather data for destinations
   */
  public getDestinationWeather(): DestinationWeather[] {
    const destinations = this.getPopularDestinations();
    const now = new Date();
    
    return destinations.map(destination => ({
      destination,
      weather: this.generateWeatherForDestination(destination, now)
    }));
  }

  /**
   * Generate weather data for a specific destination
   */
  private generateWeatherForDestination(destination: PopularDestination, baseDate: Date): WeatherInfo {
    // Base temperatures by season and location
    const seasonalTemp = this.getSeasonalTemperature(destination, baseDate);
    const currentTemp = seasonalTemp + (Math.random() * 6 - 3); // ±3°C variation
    
    const conditions = ['Sunny', 'Partly Cloudy', 'Cloudy', 'Rainy'];
    const icons = ['☀️', '⛅', '☁️', '🌧️'];
    const currentConditionIndex = Math.floor(Math.random() * conditions.length);
    
    const forecast = [];
    for (let i = 1; i <= 3; i++) {
      const forecastDate = new Date(baseDate);
      forecastDate.setDate(baseDate.getDate() + i);
      
      const dayTemp = seasonalTemp + (Math.random() * 8 - 4);
      const conditionIndex = Math.floor(Math.random() * conditions.length);
      
      forecast.push({
        date: forecastDate,
        high: Math.round(dayTemp + 3),
        low: Math.round(dayTemp - 5),
        condition: conditions[conditionIndex] || 'Sunny',
        icon: icons[conditionIndex] || '☀️',
        precipitation: conditionIndex === 3 ? Math.round(Math.random() * 80 + 20) : Math.round(Math.random() * 20)
      });
    }

    return {
      current: {
        temperature: Math.round(currentTemp),
        condition: conditions[currentConditionIndex] || 'Sunny',
        icon: icons[currentConditionIndex] || '☀️',
        humidity: Math.round(Math.random() * 40 + 40), // 40-80%
        windSpeed: Math.round(Math.random() * 20 + 5) // 5-25 km/h
      },
      forecast,
      lastUpdated: new Date()
    };
  }

  /**
   * Get seasonal temperature for a destination
   */
  private getSeasonalTemperature(destination: PopularDestination, date: Date): number {
    const month = date.getMonth(); // 0-11
    const isWinter = month === 11 || month <= 1;
    const isSpring = month >= 2 && month <= 4;
    const isSummer = month >= 5 && month <= 7;

    // Base temperatures by city and season
    const tempMap: { [key: string]: { winter: number; spring: number; summer: number; autumn: number } } = {
      'paris-fr': { winter: 5, spring: 15, summer: 25, autumn: 12 },
      'london-uk': { winter: 4, spring: 12, summer: 20, autumn: 10 },
      'rome-it': { winter: 10, spring: 18, summer: 28, autumn: 16 },
      'barcelona-es': { winter: 12, spring: 18, summer: 26, autumn: 17 },
      'amsterdam-nl': { winter: 3, spring: 12, summer: 20, autumn: 9 },
      'prague-cz': { winter: 0, spring: 14, summer: 24, autumn: 8 }
    };

    const cityTemps = tempMap[destination.id];
    const fallbackTemps = { winter: 5, spring: 15, summer: 25, autumn: 12 }; // Paris default
    
    const temps = cityTemps || fallbackTemps;
    
    if (isWinter) return temps.winter;
    if (isSpring) return temps.spring;
    if (isSummer) return temps.summer;
    return temps.autumn;
  }

  /**
   * Get travel tips articles
   */
  public getTravelTips(): TravelTip[] {
    return [
      {
        id: 'packing-essentials',
        title: 'Travel Essentials to Pack',
        excerpt: 'Discover our complete list of must-haves for light and efficient travel.',
        content: 'Traveling with optimized luggage can transform your experience. Here are our expert tips to forget nothing while avoiding overweight...',
        category: 'packing',
        readTime: 5,
        image: '/assets/images/tips/packing.jpg',
        publishDate: new Date('2024-10-15'),
        tags: ['luggage', 'organization', 'travel'],
        author: { name: 'Marie Dubois', avatar: '/assets/images/authors/marie.jpg' }
      },
      {
        id: 'budget-travel-tips',
        title: 'Budget Travel: Our Tips',
        excerpt: 'How to explore Europe without breaking the bank? Our advice for saving on accommodation, transport and meals.',
        content: 'Travel doesn\'t have to cost a fortune. With these proven strategies, you can discover beautiful European destinations...',
        category: 'budget',
        readTime: 7,
        image: '/assets/images/tips/budget.jpg',
        publishDate: new Date('2024-10-12'),
        tags: ['savings', 'budget', 'tips'],
        author: { name: 'Pierre Martin', avatar: '/assets/images/authors/pierre.jpg' }
      },
      {
        id: 'safety-abroad',
        title: 'Travel Safety: Complete Guide',
        excerpt: 'Stay safe during your travels with our practical advice on prevention and emergency management.',
        content: 'Safety should be your top priority when traveling. This guide covers all essential aspects for traveling with peace of mind...',
        category: 'safety',
        readTime: 8,
        image: '/assets/images/tips/safety.jpg',
        publishDate: new Date('2024-10-10'),
        tags: ['security', 'emergency', 'prevention'],
        author: { name: 'Sophie Laurent', avatar: '/assets/images/authors/sophie.jpg' }
      },
      {
        id: 'cultural-etiquette',
        title: 'Cultural Etiquette in Europe',
        excerpt: 'Avoid cultural faux pas and show respect for local traditions with our guide to good manners.',
        content: 'Each European country has its own cultural codes. Learn the basics of etiquette to create authentic connections...',
        category: 'culture',
        readTime: 6,
        image: '/assets/images/tips/culture.jpg',
        publishDate: new Date('2024-10-08'),
        tags: ['culture', 'etiquette', 'respect'],
        author: { name: 'Jean Moreau', avatar: '/assets/images/authors/jean.jpg' }
      },
      {
        id: 'transport-guide',
        title: 'European Transport Guide',
        excerpt: 'Train, plane, bus: how to choose the best means of transport according to your destination and budget.',
        content: 'Europe offers an exceptional transport network. Discover how to optimize your travels to save time and money...',
        category: 'transport',
        readTime: 9,
        image: '/assets/images/tips/transport.jpg',
        publishDate: new Date('2024-10-05'),
        tags: ['transport', 'train', 'plane'],
        author: { name: 'Claire Rousseau', avatar: '/assets/images/authors/claire.jpg' }
      }
    ];
  }

  /**
   * Get enhanced special offers with validity dates
   */
  public getEnhancedOffers(): EnhancedOffer[] {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
    const twoMonthsLater = new Date(now.getFullYear(), now.getMonth() + 2, now.getDate());
    const threeMonthsLater = new Date(now.getFullYear(), now.getMonth() + 3, now.getDate());

    return [
      {
        id: 'paris-weekend-special',
        title: 'Romantic Weekend in Paris',
        description: '3 days / 2 nights stay with 4* hotel and breakfast included',
        longDescription: 'Discover the magic of Paris during a romantic weekend. Stay in a 4-star hotel in the heart of the capital, continental breakfast included, and privileged access to a Seine cruise.',
        price: {
          amount: 299,
          currency: 'EUR',
          originalPrice: 399,
          discount: 25
        },
        image: '/assets/images/offers/romantic-weekend-in-paris.jpg',
        destination: 'Paris, France',
        type: 'package',
        validity: {
          startDate: now,
          endDate: nextMonth,
          bookingDeadline: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 15)
        },
        conditions: [
          'Valid for stays Monday to Thursday',
          'Minimum 7 days advance booking',
          'Non-refundable, modification possible under conditions'
        ],
        highlights: [
          '4* hotel in city center',
          'Continental breakfast',
          'Free Seine cruise',
          'Free cancellation 48h'
        ],
        availability: 'medium',
        rating: 4.6,
        reviewCount: 1247,
        ctaButton: {
          text: 'Book now',
          onClick: () => console.log('Paris weekend booking')
        }
      },
      {
        id: 'london-culture-break',
        title: 'Cultural Getaway to London',
        description: 'Flight + hotel 3 nights with museum entries included',
        longDescription: 'Immerse yourself in London\'s cultural richness. Round-trip flight, 3 nights in a 3-star hotel, and museum pass giving access to over 15 major attractions.',
        price: {
          amount: 425,
          currency: 'EUR',
          originalPrice: 525,
          discount: 19
        },
        image: '/assets/images/offers/cultural-getaway-to-london.jpg',
        destination: 'London, United Kingdom',
        type: 'package',
        validity: {
          startDate: now,
          endDate: twoMonthsLater,
          bookingDeadline: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 20)
        },
        conditions: [
          'Departures from Paris CDG only',
          'Museum pass valid 7 days',
          'Hotel subject to availability'
        ],
        highlights: [
          'Direct flight included',
          'Pass 15+ museums',
          'Hotel zone 2 London',
          'Free travel guide'
        ],
        availability: 'high',
        rating: 4.4,
        reviewCount: 892,
        ctaButton: {
          text: 'View offer',
          onClick: () => console.log('London culture booking')
        }
      },
      {
        id: 'barcelona-beach-city',
        title: 'Barcelona: City and Beach',
        description: 'Perfect combination of Catalan culture and Mediterranean relaxation',
        longDescription: 'The best of both worlds: explore Gaudí\'s unique architecture and relax on Costa Brava beaches. Flexible stay with à la carte activities.',
        price: {
          amount: 380,
          currency: 'EUR',
          originalPrice: 480,
          discount: 21
        },
        image: '/assets/images/offers/barcelona-city-and-beach.jpg',
        destination: 'Barcelona, Spain',
        type: 'package',
        validity: {
          startDate: now,
          endDate: threeMonthsLater,
          bookingDeadline: new Date(now.getFullYear(), now.getMonth() + 1, now.getDate())
        },
        conditions: [
          'Minimum 4 nights stay',
          'Weekend supplement +50€',
          'Local transport not included'
        ],
        highlights: [
          'Beachfront hotel',
          'Sagrada Familia visit',
          'Tapas tasting',
          'City bikes included'
        ],
        availability: 'low',
        rating: 4.7,
        reviewCount: 1456,
        ctaButton: {
          text: 'Discover',
          onClick: () => console.log('Barcelona beach booking')
        }
      }
    ];
  }
}